import { useTranslation } from "react-i18next";
import useWindowSize from '../../Helpers/useWindowSize';
import { useEffect, useState, ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { Link } from "react-router-dom";

// Mobile only: the cat is drawn inside a desktop-like "frame" and the frame is
// shrunk with CSS, so it looks exactly like desktop, just smaller.
const FRAME_SCALE = 0.85; // how much the frame is shrunk on mobile (cat size). Frame width = screen width / this
const FRAME_W_FALLBACK = 420; // used only until the window width is known
const FRAME_H = 640;   // virtual panel height (cat scale is computed from this)
const CROP_H = 310;    // visible part of the frame (cat + string), rest is cropped
const CAT_OFFSET_X_RATIO = -0.2; // sideways nudge as a share of the frame width (+ = right, - = left); tune this if the cat is not centred
const CAT_OFFSET_Y = -50; // moves the cat up (-) / down (+) inside the frame, px

// Cat markup: legs and tail are the same div nested repeatedly (the CSS animates each level).
const LEG_DEPTH = 16;  // number of nested .cat-leg divs, the innermost one holds the paw
const TAIL_DEPTH = 15; // number of nested .cat-tail divs, the innermost one holds .cat-tail.-end

// Progress weights per step (total = 100)
const WEIGHTS = [10, 10, 70, 10];
const CUMULATIVE_WEIGHTS = WEIGHTS.reduce<number[]>((acc, w, i) => {
    acc[i] = (acc[i - 1] || 0) + w;
    return acc;
}, []);

const Nested = ({ depth, className, children }: { depth: number; className: string; children: ReactNode }) => {
    if (depth <= 0) return <>{children}</>;
    return (
        <div className={className}>
            <Nested depth={depth - 1} className={className}>{children}</Nested>
        </div>
    );
};

const CheckIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#c1e7c3"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M3 12l5 5l9-9" />
        <path d="M9 12l5 5l9-9" />
    </svg>
);

const SpinnerIcon = () => (
    <svg
        className="spinner"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        {/* The faint track circle in the background */}
        <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="2.5"
            opacity="0.2"
        />

        {/* The main spinning arc */}
        <path
            d="M12 2 a10 10 0 0 1 10 10"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
        />
    </svg>
);

const HiddenIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 12C3 12 5 8 12 8C19 8 21 12 21 12M3 12C3 12 5 16 12 16C19 16 21 12 21 12M3 12H21" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const WarningIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const PendingIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="12" cy="12" r="10" stroke="#4B5563" strokeWidth="2" />
    </svg>
);

interface propTypes {
    isWhiteLabel: boolean // base64 image of wcag
}

// component to render loader
const CustomLoader = (props: propTypes) => {

    const { t } = useTranslation();

    // NOTE: useWindowSize must return windowWidth as well as windowHeight
    const { windowHeight, windowWidth } = useWindowSize();

    const { isWhiteLabel } = props;

    const { scaningProcessPer, websiteURL, standard, token } = useSelector((store: StoreProptypes) => store);

    const [scale, setScale] = useState(1);
    const [currentStep, setCurrentStep] = useState(1);

    const isMobile = !!windowWidth && windowWidth < 992; // matches SCSS breakpoint

    const dispatch = useDispatch();

    const handleExit = (e: any) => {
        e.preventDefault();
        dispatch({ type: "SET_SCANING_PROCESS", payload: false });
        dispatch({ type: "SET_RESCANING_PROCESS", payload: false });
        dispatch({ type: "SET_SCANING_PROCESS_PER", payload: 0 });

        window.location.href = token ? `/?token=${token}` : '/';
    };

    // Desktop: cat scale follows window height
    useEffect(() => {
        if (windowHeight) {
            const temp = (windowHeight * .44) / 480;
            setScale(temp);
        }
    }, [windowHeight])

    // Current step from scan progress.
    // findIndex returns -1 once progress reaches 100, so stay on the last step.
    useEffect(() => {
        const idx = CUMULATIVE_WEIGHTS.findIndex(cum => scaningProcessPer < cum);
        setCurrentStep(idx === -1 ? WEIGHTS.length : idx + 1);
    }, [scaningProcessPer]);

    // Mobile: scale of the shrunk frame and its visible height
    // Frame width follows the device: it always spans exactly the screen width after shrinking
    const frameK = FRAME_SCALE;
    const frameW = windowWidth ? Math.round(windowWidth / FRAME_SCALE) : FRAME_W_FALLBACK;
    const catOffsetX = Math.round(frameW * CAT_OFFSET_X_RATIO);
    const frameCatScale = (FRAME_H * .44) / 480;

    const steps = [
        {
            title: t("Starting Scan"),
            description: t("Collecting website data, detecting page structure, and initializing the analysis engine"),
            subTitle: t("Scanning")
        },
        {
            title: t("Fetching Content"),
            description: t("Gathering images, text, links, and code elements to prepare for scanning"),
            subTitle: t("Fetching")
        },
        {
            title: t("Running Accessibility Checks"),
            description: t("Evaluating visuals, navigation, content clarity, and code to identify accessibility issues"),
            subTitle: t("Running")
        },
        {
            title: t("Preparing Your Report"),
            description: t("Summarizing findings, determining your accessibility score, and organizing recommendations"),
            subTitle: t("Preparing")
        }
    ];

    const checkItems = [
        { id: 1, icon: <CheckIcon />, text: t("Labels for interactive elements") },
        { id: 2, icon: <HiddenIcon />, text: t("Hidden elements ignored by keyboard") },
        { id: 3, icon: <CheckIcon />, text: t("Color contrast is sufficient") },
        { id: 4, icon: <WarningIcon />, text: t("Alt text for images is descriptive") },
        { id: 5, icon: <CheckIcon />, text: t("Keyboard navigation is logical") }
    ];

    const scrollingItems = [...checkItems, ...checkItems];

    /**
     * Determines the status of a step based on its index compared to the current active step.
     * @param {number} stepIndex - The index of the step to check.
     * @returns {'completed' | 'in-progress' | 'pending'} - The status of the step.
     */
    const getStepStatus = (stepIndex: number) => {
        if (stepIndex + 1 < currentStep) {
            return 'completed';
        }
        if (stepIndex + 1 === currentStep) {
            return 'in-progress';
        }
        return 'pending';
    };

    // Cat artwork. `s` is the scale applied to it.
    const renderCat = (s: number) => (
        <div className="cat-container">
            <div className="all-wrap">
                <div className="scale-wrapper" style={{ transform: `scale(${s})` }}>
                    <div className="all">
                        <div className="yarn"></div>
                        <div className="cat-wrap">
                            <div className="cat">
                                <div className="cat-upper">
                                    <div className="cat-leg"></div>
                                    <div className="cat-leg"></div>
                                    <div className="cat-head">
                                        <div className="cat-ears">
                                            <div className="cat-ear"></div>
                                            <div className="cat-ear"></div>
                                        </div>
                                        <div className="cat-face">
                                            <div className="cat-eyes"></div>
                                            <div className="cat-mouth"></div>
                                            <div className="cat-whiskers"></div>
                                        </div>
                                    </div>
                                </div>
                                <div className="cat-lower-wrap">
                                    <div className="cat-lower">
                                        <Nested depth={LEG_DEPTH} className="cat-leg">
                                            <div className="cat-paw"></div>
                                        </Nested>
                                        <Nested depth={LEG_DEPTH} className="cat-leg">
                                            <div className="cat-paw"></div>
                                        </Nested>
                                        <Nested depth={TAIL_DEPTH} className="cat-tail">
                                            <div className="cat-tail -end"></div>
                                        </Nested>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // ===================== MOBILE =====================
    if (isMobile) {
        return (
            <div className="accessibility-checker accessibility-checker--mobile">
                {/* Header */}
                <header className="m-loader__header">
                    <p className="m-loader__eyebrow">{t('Scanning')}</p>
                    <h1 className="m-loader__url" title={websiteURL ?? ''}>{websiteURL}</h1>
                    <p className="m-loader__standard">
                        {t('Your website is being evaluated against {{standard}} & other international accessibility standards', { standard })}.
                    </p>

                    {/* Progress bar */}
                    <div className="m-loader__progress">
                        <div className="m-loader__progress-row">
                            <span>{t('Progress')}</span>
                            <span>{scaningProcessPer}%</span>
                        </div>
                        <div
                            className="m-loader__bar"
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={scaningProcessPer}
                            aria-label={t('Progress')}
                        >
                            <motion.div
                                className="m-loader__bar-fill"
                                initial={{ width: "0%" }}
                                animate={{ width: `${scaningProcessPer}%` }}
                                transition={{ duration: 0.5, ease: "easeInOut" }}
                            />
                        </div>
                    </div>
                </header>

                {/* Cat + text, same look as desktop */}
                <div className="m-loader__stage checker-content">
                    <div
                        className="cat-mobile-slot"
                        aria-hidden="true"
                        style={{
                            height: CROP_H * frameK,
                            WebkitMaskImage: "linear-gradient(to bottom, #000 85%, transparent)",
                            maskImage: "linear-gradient(to bottom, #000 85%, transparent)",
                        }}
                    >
                        <div
                            // same flex context the cat has on desktop, so it centres the same way
                            className="cat-mobile-frame checker-content d-flex flex-column justify-content-center align-items-center"
                            style={{
                                width: frameW,
                                height: FRAME_H,
                                transform: `translate(-50px)`,
                            }}
                        >
                            {renderCat(frameCatScale)}
                        </div>
                    </div>

                    <div className="main-checks-container">
                        <h2 className="main-checks-title" role="status" aria-live="polite">
                            {steps[currentStep - 1]?.subTitle || t('Scanning')}....
                        </h2>

                        <div className="list-viewport" aria-hidden="true">
                            <ul className="scrolling-list">
                                {scrollingItems.map((item, index) => (
                                    <li key={index} className="check-item">
                                        <span className="check-item-icon">{item.icon}</span>
                                        <span>{item.text}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <footer className="m-loader__footer">
                    {!isWhiteLabel && (
                        <a href="https://www.skynettechnologies.com/report-accessibility-problem" target="_blank" rel="noreferrer">
                            {t('Need support')}?
                        </a>
                    )}
                    <Link to={token ? `/?token=${token}` : '/'} onClick={handleExit}>
                        {t('Exit Scan')}
                    </Link>
                </footer>
            </div>
        );
    }

    // ===================== DESKTOP (unchanged layout) =====================
    return (
        <>
            {/* "vh-100" removed: it forces height: 100vh !important and clips content on mobile */}
            <div className="accessibility-checker d-flex">
                {/* Sidebar */}
                <div className="checker-sidebar p-4 text-white d-flex flex-column justify-content-between">
                    <div>
                        <h6 className="fs-3 website-ellipsis">
                            {t('Scanning')} <span className="website-ellipsis">{websiteURL}</span>
                        </h6>

                        <p className="small mt-3 text-tertiary">
                            {t('Your website is being evaluated against {{standard}} & other international accessibility standards', {
                                standard: standard
                            })}.
                        </p>

                        {/* Progress Bar */}
                        <div className="mt-4">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span>{t('Progress')}</span>
                                <span>{scaningProcessPer}%</span>
                            </div>
                            <div className="progress" style={{ height: "8px" }}>
                                <motion.div
                                    className="progress-bar progress-bar-striped"
                                    role="progressbar"
                                    style={{ backgroundColor: '#c1e7c3' }}
                                    initial={{ width: "0%" }}
                                    animate={{ width: `${scaningProcessPer}%` }}
                                    transition={{ duration: 0.5, ease: "easeInOut" }}
                                />
                            </div>
                        </div>

                        {/* Dynamic Step List */}
                        <div className="progress-container mt-3" role="status" aria-live="polite">
                            <ul className="progress-steps-list">
                                {steps.map((step, index) => {
                                    const status = getStepStatus(index);

                                    return (
                                        <li key={index} className={`progress-step ${status}`}>
                                            <div className="step-icon">
                                                {status === "completed" && <CheckIcon />}
                                                {status === "in-progress" && <SpinnerIcon />}
                                                {status === "pending" && <PendingIcon />}
                                            </div>
                                            <div className="step-content">
                                                <h3 className="step-title">{step.title}</h3>
                                                <AnimatePresence mode="wait">
                                                    {status === "in-progress" && (
                                                        <motion.p
                                                            key="desc"
                                                            className="step-description"
                                                            initial={{ opacity: 0, height: 0 }}
                                                            animate={{ opacity: 1, height: "auto" }}
                                                            exit={{ opacity: 0, height: 0 }}
                                                            transition={{ duration: 0.3, ease: "easeInOut" }}
                                                        >
                                                            {step.description}
                                                        </motion.p>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>

                    <div className="text-center small">
                        {!isWhiteLabel && (
                            <a href="https://www.skynettechnologies.com/report-accessibility-problem" target="_blank" rel="noreferrer" className="text-decoration-none exit-scan">
                                {t('Need support')}?{" "}
                            </a>
                        )}
                        <Link to={token ? `/?token=${token}` : '/'} onClick={handleExit} className="text-decoration-none exit-scan">
                            {t('Exit Scan')}
                        </Link>
                    </div>
                </div>

                {/* Right Section */}
                <div className="checker-content flex-grow-1 d-flex flex-column justify-content-center align-items-center">

                    {renderCat(scale)}

                    <div className="main-checks-container">

                        <h2 className="main-checks-title">{steps[currentStep - 1]?.subTitle || t('Scanning')}....</h2>

                        {/* This div acts as a viewport, hiding the overflowing content */}
                        <div className="list-viewport">
                            {/* This list contains the duplicated items and is the element we animate */}
                            <ul className="scrolling-list">
                                {scrollingItems.map((item, index) => (
                                    <li key={index} className="check-item">
                                        <span className="check-item-icon">{item.icon}</span>
                                        <span>{item.text}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default CustomLoader